// rfArcGeometry — Radius-to-Fix (RF) leg path rendering.
//
// RF legs in CIFP are true constant-radius arcs. Given:
//   start  : the leg's FROM point (the prior leg's terminator)
//   end    : the leg's fix (TO point)
//   center : the RF leg's center fix
//   turn   : 'L' or 'R' (turn direction)
//
// the path is uniquely determined. We sample the arc into N points by
// stepping through the angular sweep at constant radius.
//
// If center coordinates are missing we fall back to a great-circle
// straight line — same as before this module existed.

import { bearingDeg, destinationPoint, greatCircleNm } from './geo';

export interface RfArcParams {
  startLat: number;
  startLon: number;
  endLat: number;
  endLon: number;
  centerLat: number;
  centerLon: number;
  turn: 'L' | 'R';
}

/** Return the arc as an ordered list of [lon, lat] coords.
 *  Includes the start and end points. */
export function rfArcCoords(p: RfArcParams, samples: number = 16): Array<[number, number]> {
  // Radius: use the start→center distance as authoritative. (start and
  // end should be equidistant from center; we trust start because if
  // there's a small CIFP encoding error it's preferable to land
  // exactly on the prior fix than to drift.)
  const radiusNm = greatCircleNm(p.centerLat, p.centerLon, p.startLat, p.startLon);
  const startBearing = bearingDeg(p.centerLat, p.centerLon, p.startLat, p.startLon);
  const endBearing   = bearingDeg(p.centerLat, p.centerLon, p.endLat,   p.endLon);

  // Compute signed sweep angle in the requested direction.
  let sweep: number;
  if (p.turn === 'R') {
    // Right turn = clockwise from start → end = end - start, mod 360.
    sweep = (endBearing - startBearing + 360) % 360;
  } else {
    // Left turn = counter-clockwise = start - end, mod 360.
    sweep = (startBearing - endBearing + 360) % 360;
  }
  // Degenerate / single-point case.
  if (sweep < 0.5) {
    return [[p.startLon, p.startLat], [p.endLon, p.endLat]];
  }

  const out: Array<[number, number]> = [];
  for (let i = 0; i <= samples; i++) {
    const frac = i / samples;
    const stepDeg = sweep * frac;
    const bearing = p.turn === 'R'
      ? (startBearing + stepDeg + 360) % 360
      : (startBearing - stepDeg + 360) % 360;
    const pt = destinationPoint(p.centerLat, p.centerLon, bearing, radiusNm);
    out.push([pt.lon, pt.lat]);
  }
  return out;
}
